import { doGet2, doPost2ErrorHandling } from 'services/common'
import { removeEmptyObjects } from 'util/removeEmptyObjects'
import { getIncommUtmPayload, IncommUtmPayload } from './utm'

export const BARCODE_FORMATS = {
  CODE_128: 'CODE_128',
  QR_CODE: 'QR_CODE',
} as const

export interface GenerateBarcodeResponse {
  id?: number
  email?: string
  associatedProperty?: string
  barcodeContent?: string
  barcodeFormat?: string
  barcodeAccessToken?: string
  displayText?: string
  upc?: string
  accountNumber?: string
  created?: string
  uniqueIdentifier?: string
  offerId?: string
  quantityLimit?: number
  currentQuantity?: number
  remainingQuantity?: number
  quantityLimitReached?: boolean
  offerEnd?: string
  offerClipped?: boolean
  offerStatus?: string
  clipMessage?: string
  timestamp?: number
  status?: number
  error?: string
  message?: string
  path?: string
  isExistingBarcodeAtLimit?: boolean // Added in new API: indicates customer limit reached
  autoSendEmail?: boolean | string | number
  autoSendBarcodeEmail?: boolean | string | number
}

export interface SendBarcodeEmailPayload extends IncommUtmPayload {
  email: string
  offerId: number
  barcodeAccessToken: string
  barcodeType: 'CODE_128' | 'QR_CODE'
  retailerStore: string
  retailerStoreCashierInformation: string
  retailerStoreIconUrl: string
  turnstileToken: string
  stepId?: string
}

export interface SendBarcodeEmailResponse {
  success: boolean
  status: number
  detail: string
}

export interface AddToWalletPayload extends IncommUtmPayload {
  email: string
  offerId: number
  barcodeAccessToken: string
  barcodeType: 'CODE_128' | 'QR_CODE'
  walletType: 'google' | 'apple'
  retailerStore: string
  retailerStoreCashierInformation: string
  retailerStoreIconUrl: string
  turnstileToken: string
  stepId?: string
}

export interface AddToWalletResponseData {
  walletType: 'google' | 'apple'
  redirectUrl: string
  passId: string
}

export interface AddToWalletResponse {
  success: boolean
  status: number
  detail: AddToWalletResponseData
}

/**
 * Send barcode details to user's email
 *
 * @param payload - The email payload containing barcode and retailer information
 * @returns Promise resolving to the API response
 *
 * Error codes:
 * - 400: Invalid Turnstile or bad request
 * - 403: Barcode doesn't belong to email
 * - 404: Barcode/offer not found
 * - 500: Server/email sending failed
 */
export const sendBarcodeEmail = async (
  payload: SendBarcodeEmailPayload,
): Promise<SendBarcodeEmailResponse> => {
  const utmPayload = await getIncommUtmPayload()
  const payloadWithUtm = removeEmptyObjects({ ...utmPayload, ...payload })

  return doPost2ErrorHandling('/incomm/barcode/send-email', payloadWithUtm)
}

/**
 * Add barcode to digital wallet (Google Wallet or Apple Wallet)
 *
 * @param payload - The wallet payload containing barcode and retailer information
 * @returns Promise resolving to the API response with redirect URL
 *
 * Error codes:
 * - 400: Invalid Turnstile or bad request
 * - 403: Barcode doesn't belong to email
 * - 404: Barcode/offer not found
 * - 500: Server/wallet generation failed
 */
export const addBarcodeToWallet = async (
  payload: AddToWalletPayload,
): Promise<AddToWalletResponse> => {
  const utmPayload = await getIncommUtmPayload()
  const payloadWithUtm = removeEmptyObjects({ ...utmPayload, ...payload })

  return doPost2ErrorHandling('/incomm/barcode/add-to-wallet', payloadWithUtm)
}

export interface ChangeRetailerOptionsResponse {
  email: string
  offerId: number
  barcodeText: string
  offerEnd: string
  currentStoreId: string | null
  validStoreIds: string[]
  offerTitle?: string
  offerDescription?: string
  offerBannerImageUrl?: string
  offerSupportInformation?: string
  offerLegalInformation?: string
  autoSendBarcodeEmail?: boolean
}

export interface ChangeRetailerPayload {
  email: string
  barcodeAccessToken: string
  newStoreId: string
  turnstileToken: string
  source?: string
}

export interface ChangeRetailerResponse {
  barcodeAccessToken: string
  email: string
  offerId: number
  selectedStoreId: string
  barcodeFormat: string
  barcodeText: string
  offerEnd: string
}

/**
 * Fetch the change-retailer options for an existing barcode: its current retailer,
 * the retailers valid for its offer, and the display data needed to render the barcode.
 * Validates barcode ownership server-side via accessToken + email.
 */
export const getChangeRetailerOptions = async (
  barcodeAccessToken: string,
  email: string,
): Promise<ChangeRetailerOptionsResponse> => {
  // doGet2 returns the raw Response — parse JSON here (mirrors the banner-stores client)
  const response = await doGet2('/incomm/barcode/change-retailer-options', {
    barcodeAccessToken,
    email,
  })
  if (response.ok) {
    return response.json()
  }
  throw new Error(`API Error: ${response.status}`)
}

/**
 * Switch the selected retailer on an existing barcode without generating a new code.
 * The new retailer must be valid for the barcode's offer.
 *
 * Error codes:
 * - 400: Invalid Turnstile, or retailer not valid for the offer
 * - 403: Barcode doesn't belong to email
 * - 404: Barcode/offer not found
 * - 429: Rate limit exceeded
 */
export const changeRetailer = async (
  payload: ChangeRetailerPayload,
): Promise<ChangeRetailerResponse> => {
  // doPost2ErrorHandling resolves to { status, success, detail }; the parsed body is in `detail`
  const response = await doPost2ErrorHandling(
    '/incomm/barcode/change-retailer',
    payload,
  )
  return response.detail
}
